import { useState, useEffect } from 'react';

import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import VirusScansSection from './VirusScansSection';
import { antivirusApis } from '../../../apis/antivirusApis';
import { INSTANCE_PLANS } from '../../../constants';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { sentenceCase } from '../../../utils/utils';
import AlertMessage from '../../elements/alert-message/AlertMessage';
import Loading from '../../elements/loading/Loading';
import Text from '../../elements/text/Text';
import TabPanel from '../instance-tabs/TabPanel';

const AntivirusTab = ({ instanceSize }) => {
  const { projectId, instanceId, gpsId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [antivirus, setAntivirus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchAntivirus = async () => {
    setLoading(true);
    try {
      const { data: antivirusRes } = await antivirusApis.getAntivirusSettings(
        projectId,
        instanceId || gpsId
      );
      setAntivirus(antivirusRes.content[0]);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAntivirus();
  }, [projectId, instanceId, gpsId]);

  const handleAntivirusChange = async (event) => {
    const setting = event.target.checked;

    if (setting) {
      try {
        const updatedSettings = await antivirusApis.enableAntivirus(
          projectId,
          instanceId || gpsId
        );
        setAntivirus(updatedSettings.data);
        showToast(
          TOAST_TYPE.SUCCESS,
          t('antivirusEnableSuccess', { ns: 'notifications' })
        );
      } catch (error) {
        showToast(
          TOAST_TYPE.ERROR,
          t('antivirusEnableError', { ns: 'notifications' })
        );
      }
    } else {
      try {
        const updatedSettings = await antivirusApis.disableAntivirus(
          projectId,
          instanceId || gpsId,
          antivirus.id
        );
        setAntivirus(updatedSettings.data);
        showToast(
          TOAST_TYPE.SUCCESS,
          t('antivirusDisableSuccess', { ns: 'notifications' })
        );
      } catch (error) {
        showToast(
          TOAST_TYPE.ERROR,
          t('antivirusDisableError', { ns: 'notifications' })
        );
      }
    }
  };

  if (loading) return <Loading />;

  if (error)
    return (
      <AlertMessage
        severity="error"
        message={sentenceCase(
          t('loading', {
            ns: 'errors',
            content: t('antivirus.settings', { ns: 'instance' }),
          })
        )}
      />
    );

  return (
    <TabPanel value={2} index={2}>
      <Text sx={{ mb: 2 }}>
        {t('settings.antivirus', { ns: 'descriptions' })}
      </Text>
      <Text subtitle sx={{ fontWeight: 600 }}>
        {t('antivirus.virusScan', { ns: 'instance' })}
      </Text>
      <FormControlLabel
        label={
          antivirus?.enabled
            ? t('antivirus.enabled', { ns: 'instance' })
            : t('antivirus.disabled', { ns: 'instance' })
        }
        labelPlacement="end"
        sx={{ mb: 2, ml: 2 }}
        control={
          <Switch
            name="antivirus"
            checked={antivirus?.enabled || false}
            color="primary"
            onChange={handleAntivirusChange}
            slotProps={{
              input: {
                'aria-label': 'antivirus switch',
              },
            }}
            disabled={instanceSize === INSTANCE_PLANS.SMALL}
          />
        }
      />

      <VirusScansSection antivirus={antivirus} instanceSize={instanceSize} />
    </TabPanel>
  );
};

AntivirusTab.propTypes = {
  instanceSize: PropTypes.string.isRequired,
};

export default AntivirusTab;
